/**
 * Public Wizard Page
 *
 * Publik wizard som fungerar utan inloggning.
 * Återanvänder de riktiga steg-komponenterna med override-callbacks för localStorage.
 *
 * GÄST-LÄGE (6 steg):
 * Steg 1: Företag → definiera företag via Bolagsverket-lookup
 * Steg 2: Bokföringsdata → välj datakälla (SIE-fil, manuell inmatning, integration)
 * Steg 3-5: Lokal state (localStorage) via riktiga steg-komponenter
 * Steg 6: Kräver inloggning → convert → redirect till dashboard wizard
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  FileText,
  CheckCircle,
  AlertTriangle,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Lock,
  LogIn,
  Mail,
  RefreshCw,
} from "lucide-react";
import {
  wizardApi,
  authApi,
  type AnnualReport,
  type BoardMember,
  type Company,
  type UserInput,
  type User,
} from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { AuthModal, type AuthModalView } from "@/components/auth/AuthModal";
import {
  saveGuestWizard,
  loadGuestWizard,
  clearGuestWizard,
  type GuestWizardState,
  type GuestBoardMember,
  type GuestCompany,
} from "@/lib/guest-wizard";

// Real step components
import { WizardLayout } from "./WizardLayout";
import { GuestCompanyStep } from "./GuestCompanyStep";
import { Step1DataSource } from "./Step1DataSource";
import { Step2Overview } from "./Step2Overview";
import { Step2_5CashFlow } from "./Step2_5CashFlow";
import { Step3Complete } from "./Step3Complete";
import { Step4Disposition } from "./Step4Disposition";
import { PageMeta, PUBLIC_PAGE_META } from "@/lib/seo";
import { calculateTriggeredNotes, applyOverridesToSieData } from "@/lib/manual-entry";

// K3 step order for public wizard (adds cash flow step between 3 and 4)
// K2: 1 → 2 → 3 → 4 → 5 → 6
// K3: 1 → 2 → 3 → 3.5 → 4 → 5 → 6
const PUBLIC_K3_STEP_ORDER = [1, 2, 3, 3.5, 4, 5, 6];
const PUBLIC_K2_STEP_ORDER = [1, 2, 3, 4, 5, 6];

function getPublicStepOrder(accountingStandard: "K2" | "K3"): number[] {
  return accountingStandard === "K3" ? PUBLIC_K3_STEP_ORDER : PUBLIC_K2_STEP_ORDER;
}

function getNextPublicStep(currentStep: number, accountingStandard: "K2" | "K3"): number | null {
  const order = getPublicStepOrder(accountingStandard);
  const index = order.indexOf(currentStep);
  if (index >= 0 && index < order.length - 1) {
    return order[index + 1];
  }
  return null;
}

function getPreviousPublicStep(currentStep: number, accountingStandard: "K2" | "K3"): number | null {
  const order = getPublicStepOrder(accountingStandard);
  const index = order.indexOf(currentStep);
  if (index > 0) {
    return order[index - 1];
  }
  return null;
}

/**
 * Build a virtual AnnualReport from guest wizard state
 */
function buildVirtualReport(
  guestState: GuestWizardState | null,
  userInput: Record<string, unknown>,
  currentStep: number
): AnnualReport {
  // Use accounting standard from userInput if set, otherwise default to K2
  const accountingStandard = (userInput.accountingStandard as "K2" | "K3") || "K2";

  return {
    id: "guest",
    companyId: "guest",
    fiscalYearStart: guestState?.fiscalYear?.start || "",
    fiscalYearEnd: guestState?.fiscalYear?.end || "",
    status: "draft",
    currentStep,
    accountingStandard,
    createdAt: new Date().toISOString(),
    completedAt: null,
    exportedAt: null,
    updatedAt: new Date().toISOString(),
    sieFileId: guestState?.parsedSieData ? "guest-sie" : null,
    parsedSieData: guestState?.parsedSieData || null,
    userInput: userInput as UserInput,
    calculations: guestState?.calculations || null,
    validationResult: null,
  };
}

/**
 * Build a virtual Company from guest wizard state
 * Uses the company defined in Step 1 (GuestCompanyStep) or falls back to SIE data
 */
function buildVirtualCompany(guestState: GuestWizardState | null): Company {
  // Prioritize user-defined company (from Step 1)
  if (guestState?.company) {
    return {
      id: "guest",
      organizationNumber: guestState.company.organizationNumber,
      name: guestState.company.name,
      type: guestState.company.type,
      seatMunicipality: guestState.company.seatMunicipality || null,
      address: guestState.company.address || null,
      postalCode: guestState.company.postalCode || null,
      city: guestState.company.city || null,
      financialHealthProfile: null,
      createdAt: new Date().toISOString(),
    };
  }

  // Fallback to SIE-extracted companyInfo (backward compatibility)
  return {
    id: "guest",
    organizationNumber: guestState?.companyInfo?.organizationNumber || "",
    name: guestState?.companyInfo?.name || "Företag",
    type: "AB",
    seatMunicipality: null,
    address: null,
    postalCode: null,
    city: null,
    financialHealthProfile: null,
    createdAt: new Date().toISOString(),
  };
}

export function PublicWizardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user, refreshUser } = useAuth();

  const [currentStep, setCurrentStep] = useState(1);
  const [isConverting, setIsConverting] = useState(false);
  const [convertError, setConvertError] = useState<string | null>(null);

  // Guest wizard state
  const [guestState, setGuestState] = useState<GuestWizardState | null>(null);

  // Auth modal
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authView, setAuthView] = useState<AuthModalView>("register");

  // User input (local only)
  const [userInput, setUserInput] = useState<Record<string, unknown>>({});

  // Board members (local only, for guest mode)
  const [guestBoardMembers, setGuestBoardMembers] = useState<BoardMember[]>([]);

  // Handle OAuth return - when source=integration is in URL, go to step 2 (data source)
  // This runs BEFORE loading saved state so OAuth return takes priority
  useEffect(() => {
    const sourceParam = searchParams.get("source");
    if (sourceParam === "integration") {
      // Go to step 2 (data source selection) where IntegrationSelector is rendered
      setCurrentStep(2);
      // Clear the source param from URL (Step1DataSource will handle the rest)
      // Don't clear it here - let Step1DataSource use it for its internal state
    }
  }, [searchParams]);

  // Load saved state on mount
  useEffect(() => {
    const saved = loadGuestWizard();
    if (saved) {
      setGuestState(saved);
      setUserInput((saved.userInput as Record<string, unknown>) || {});
      // Only restore step from localStorage if NOT returning from OAuth
      const sourceParam = searchParams.get("source");
      if (saved.currentStep > 1 && sourceParam !== "integration") {
        setCurrentStep(saved.currentStep);
      }
      // Restore board members from localStorage
      // Om personnummer saknas används tom sträng - UI-validering fångar det vid sparande
      if (saved.boardMembers?.length > 0) {
        setGuestBoardMembers(
          saved.boardMembers.map((m) => ({
            id: m.id,
            companyId: "guest",
            name: m.name,
            role: m.role,
            personalId: m.personalId || "", // Tom sträng om saknas (backward compat)
            createdAt: new Date().toISOString(),
          }))
        );
      }
    }
  }, [searchParams]);

  // If user becomes authenticated AND verified while on step 6, auto-convert
  useEffect(() => {
    if (isAuthenticated && user?.emailVerified && currentStep === 6 && guestState?.parsedSieData) {
      handleConvert();
    }
  }, [isAuthenticated, user?.emailVerified]);

  // ============================================================================
  // Callbacks passed to step components
  // ============================================================================

  /** Navigate between steps (6-7 steps in guest mode depending on K2/K3) */
  const handleNavigateStep = useCallback(
    (step: number | string) => {
      // Convert to number for validation and state (guest wizard uses numeric steps)
      const stepNum = typeof step === "string" ? parseFloat(step) : step;
      if (stepNum < 1 || stepNum > 7) return;

      // Step 1 is always accessible (company definition)
      // Steps 2+ require company to be defined
      if (stepNum > 1 && !guestState?.company) return;

      // Steps 3+ require SIE data
      if (stepNum > 2 && !guestState?.parsedSieData) return;

      // Steps 3+ require K2 eligibility confirmation (if fiscal year is 2026+)
      if (stepNum > 2) {
        const fiscalYearStart = guestState?.fiscalYear?.start || "";
        const k2Confirmed = !!(userInput as UserInput)?.k2Eligibility?.confirmedAt;
        if (fiscalYearStart) {
          const startDate = new Date(fiscalYearStart);
          const cutoffDate = new Date("2026-01-01");
          const is2026OrLater = startDate >= cutoffDate;
          if (is2026OrLater && !k2Confirmed) {
            // Cannot navigate to step 3+ until K2 eligibility is confirmed
            return;
          }
        }
      }

      setCurrentStep(stepNum);
      saveGuestWizard({ currentStep: stepNum });
      window.scrollTo(0, 0);
    },
    [guestState, userInput]
  );

  /** Handle company definition (Step 1) */
  const handleCompanyContinue = useCallback(
    (company: GuestCompany) => {
      const newState: Partial<GuestWizardState> = {
        company,
        currentStep: 2,
        savedAt: Date.now(),
      };

      setGuestState((prev) => prev ? { ...prev, ...newState } : { ...newState } as GuestWizardState);
      saveGuestWizard(newState);
      setCurrentStep(2);
      window.scrollTo(0, 0);
    },
    []
  );

  /** Check if fiscal year requires K2 2026 eligibility confirmation */
  const checkRequiresK2Confirmation = useCallback((fiscalYearStart: string, existingUserInput?: Record<string, unknown>): boolean => {
    if (!fiscalYearStart) return false;
    const startDate = new Date(fiscalYearStart);
    const cutoffDate = new Date("2026-01-01");
    const is2026OrLater = startDate >= cutoffDate;
    const alreadyConfirmed = !!(existingUserInput as UserInput)?.k2Eligibility?.confirmedAt;
    return is2026OrLater && !alreadyConfirmed;
  }, []);

  /** Handle SIE file upload (Step 2) */
  const handleUploadSie = useCallback(
    async (
      file: File
    ): Promise<{
      success: boolean;
      report?: AnnualReport;
      error?: string;
    }> => {
      try {
        const response = await wizardApi.parseSie(file);

        if (response.success && response.data) {
          const { parsedSieData, calculations, companyInfo, fiscalYear } = response.data;

          // Validate org number matches the defined company
          if (guestState?.company) {
            const definedOrgNr = guestState.company.organizationNumber.replace(/\D/g, "");
            const sieOrgNr = companyInfo.organizationNumber.replace(/\D/g, "");

            if (definedOrgNr !== sieOrgNr) {
              return {
                success: false,
                error: `Organisationsnumret i SIE-filen (${companyInfo.organizationNumber}) matchar inte det angivna företaget (${guestState.company.organizationNumber}). Kontrollera att du laddar upp rätt fil.`,
              };
            }
          }

          // Check if K2 eligibility confirmation is required
          // If required, stay on step 2 until user confirms in the modal
          const requiresK2 = checkRequiresK2Confirmation(fiscalYear?.start || "");
          const nextStepAfterUpload = requiresK2 ? 2 : 3;

          // Update state preserving the company from step 1
          const newState: GuestWizardState = {
            company: guestState?.company || null,
            parsedSieData,
            calculations,
            userInput: {},
            boardMembers: [],
            companyInfo,
            fiscalYear,
            currentStep: nextStepAfterUpload,
            savedAt: Date.now(),
          };

          setGuestState(newState);
          saveGuestWizard(newState);
          setUserInput({});
          setGuestBoardMembers([]);

          // Update currentStep directly to trigger re-render immediately
          // This is needed because Step1Upload's onNavigateStep may have stale closure
          setCurrentStep(nextStepAfterUpload);

          // Build virtual report with new data
          const virtualReport = buildVirtualReport(newState, {}, nextStepAfterUpload);

          return { success: true, report: virtualReport };
        } else {
          return {
            success: false,
            error:
              response.error?.message || "Kunde inte tolka SIE-filen",
          };
        }
      } catch {
        return { success: false, error: "Ett fel uppstod vid uppladdning" };
      }
    },
    [guestState?.company, checkRequiresK2Confirmation]
  );

  /** Handle saving user input (Steps 2-4) */
  const handleSaveInput = useCallback(
    async (
      data: Record<string, unknown>
    ): Promise<{
      success: boolean;
      report?: AnnualReport;
      error?: string;
    }> => {
      try {
        const newStep = (data.currentStep as number) || currentStep;
        const mergedInput = { ...userInput, ...data };

        // Remove currentStep from the saved input
        const { currentStep: _, ...inputWithoutStep } = mergedInput;

        // Recalculate triggeredNotes if overrides changed and we have parsedSieData
        // This mirrors backend behavior in annual-report.service.ts updateUserInput
        let updatedCalculations = guestState?.calculations;
        if (guestState?.parsedSieData && inputWithoutStep.overrides !== undefined) {
          const effectiveSieData = applyOverridesToSieData(
            guestState.parsedSieData,
            inputWithoutStep.overrides as Record<string, unknown>
          );
          const triggeredNotes = calculateTriggeredNotes(effectiveSieData);
          updatedCalculations = {
            keyFigures: guestState.parsedSieData.keyFigures,
            triggeredNotes,
          };
        }

        setUserInput(inputWithoutStep);

        // Build updated state with potentially recalculated triggeredNotes
        const updatedState = {
          ...guestState,
          userInput: inputWithoutStep,
          currentStep: newStep,
          ...(updatedCalculations ? { calculations: updatedCalculations } : {}),
        };
        setGuestState(updatedState as GuestWizardState);

        saveGuestWizard({
          userInput: inputWithoutStep,
          currentStep: newStep,
          ...(updatedCalculations ? { calculations: updatedCalculations } : {}),
        });

        const virtualReport = buildVirtualReport(
          updatedState as GuestWizardState,
          inputWithoutStep,
          newStep
        );

        return { success: true, report: virtualReport };
      } catch {
        return { success: false, error: "Kunde inte spara lokalt" };
      }
    },
    [guestState, userInput, currentStep]
  );

  /** Convert guest data to real account */
  const handleConvert = async () => {
    if (!guestState?.parsedSieData) return;

    setIsConverting(true);
    setConvertError(null);

    try {
      const response = await wizardApi.convert({
        parsedSieData: guestState.parsedSieData,
        userInput,
        boardMembers: guestState.boardMembers?.length > 0
          ? guestState.boardMembers.map((m) => ({
              name: m.name,
              role: m.role,
              personalId: m.personalId,
            }))
          : undefined,
      });

      if (response.success && response.data) {
        const { reportId } = response.data;
        clearGuestWizard();
        navigate(`/dashboard/wizard/${reportId}/step/5`);
      } else {
        setConvertError(
          response.error?.message || "Kunde inte spara datan"
        );
      }
    } catch {
      setConvertError("Ett fel uppstod");
      toast.error("Kunde inte spara årsredovisningen");
    } finally {
      setIsConverting(false);
    }
  };

  /** Handle board members saved locally (guest mode) */
  const handleSaveLocalBoardMembers = useCallback(
    (members: Array<{ name: string; role: string; personalId: string }>) => {
      const guestMembers: GuestBoardMember[] = members.map((m, i) => ({
        id: `local-${Date.now()}-${i}`,
        name: m.name.trim(),
        role: m.role,
        personalId: m.personalId?.trim() || null,
      }));
      saveGuestWizard({ boardMembers: guestMembers });
      setGuestState((prev) => prev ? { ...prev, boardMembers: guestMembers } : prev);
    },
    []
  );

  /** Handle board members change from BoardMembersSection (updates local state) */
  const handleGuestBoardMembersChange = useCallback(
    (members: BoardMember[]) => {
      setGuestBoardMembers(members);
    },
    []
  );

  /** Reset wizard */
  const handleReset = () => {
    clearGuestWizard();
    setGuestState(null);
    setUserInput({});
    setGuestBoardMembers([]);
    setCurrentStep(1);
  };

  // ============================================================================
  // Build virtual data objects
  // ============================================================================

  const virtualReport = buildVirtualReport(guestState, userInput, currentStep);
  const virtualCompany = buildVirtualCompany(guestState);

  const handleReportUpdate = (updatedReport: AnnualReport) => {
    console.log("[DEBUG handleReportUpdate] called");
    console.log("[DEBUG handleReportUpdate] updatedReport.parsedSieData exists:", !!updatedReport.parsedSieData);
    console.log("[DEBUG handleReportUpdate] updatedReport.currentStep:", updatedReport.currentStep);
    console.log("[DEBUG handleReportUpdate] current currentStep state:", currentStep);

    // When a step component calls onUpdate, sync our local state
    if (updatedReport.parsedSieData) {
      try {
        // Determine which calculations to use:
        // 1. If updatedReport already has calculations (e.g., from handleSaveInput with overrides applied), use those
        // 2. Otherwise, calculate from parsedSieData (but apply any existing overrides from userInput)
        const existingCalc = updatedReport.calculations as { keyFigures: unknown; triggeredNotes: string[] } | null;
        let triggeredNotes: string[];
        let keyFigures = updatedReport.parsedSieData.keyFigures;

        console.log("[DEBUG handleReportUpdate] existingCalc:", existingCalc);

        if (existingCalc?.triggeredNotes && existingCalc.triggeredNotes.length > 0) {
          // Use existing calculations (from handleSaveInput with overrides applied)
          triggeredNotes = existingCalc.triggeredNotes;
          console.log("[DEBUG handleReportUpdate] Using existing calculations");
        } else {
          // No calculations provided - calculate them
          // Check if there are overrides to apply (from userInput or updatedReport.userInput)
          const existingOverrides = (updatedReport.userInput as Record<string, unknown>)?.overrides ||
            (userInput as Record<string, unknown>)?.overrides;

          let effectiveSieData = updatedReport.parsedSieData;
          if (existingOverrides && Object.keys(existingOverrides as object).length > 0) {
            effectiveSieData = applyOverridesToSieData(updatedReport.parsedSieData, existingOverrides as Record<string, unknown>);
          }

          console.log("[DEBUG handleReportUpdate] Calculating triggered notes...");
          triggeredNotes = calculateTriggeredNotes(effectiveSieData);
          console.log("[DEBUG handleReportUpdate] Triggered notes calculated:", triggeredNotes.length);
        }

        const newGuestState = {
          ...guestState!,
          parsedSieData: updatedReport.parsedSieData,
          currentStep: updatedReport.currentStep,
          // For manual entry, also update fiscalYear from metadata
          fiscalYear: {
            start: updatedReport.parsedSieData.metadata?.fiscalYearStart || guestState?.fiscalYear?.start || "",
            end: updatedReport.parsedSieData.metadata?.fiscalYearEnd || guestState?.fiscalYear?.end || "",
          },
          // Store calculated triggered notes and keyFigures
          calculations: {
            keyFigures,
            triggeredNotes,
          },
        };
        console.log("[DEBUG handleReportUpdate] newGuestState.currentStep:", newGuestState.currentStep);
        setGuestState(newGuestState);
        // Also save to localStorage and update currentStep state
        saveGuestWizard(newGuestState);
        console.log("[DEBUG handleReportUpdate] Checking navigation condition:", updatedReport.currentStep, "!==", currentStep);
        if (updatedReport.currentStep && updatedReport.currentStep !== currentStep) {
          console.log("[DEBUG handleReportUpdate] Navigating to step:", updatedReport.currentStep);
          setCurrentStep(updatedReport.currentStep);
        } else {
          console.log("[DEBUG handleReportUpdate] NOT navigating - condition failed");
        }
      } catch (error) {
        console.error("[DEBUG handleReportUpdate] ERROR:", error);
      }
    }
    if (updatedReport.userInput) {
      setUserInput(updatedReport.userInput as Record<string, unknown>);
    }
  };

  // ============================================================================
  // Render
  // ============================================================================

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        // Step 1: Define company (guest-only)
        return (
          <GuestCompanyStep
            company={guestState?.company || null}
            onContinue={handleCompanyContinue}
          />
        );
      case 2:
        // Step 2: Bokföringsdata (välj datakälla: SIE, manuell, integration)
        return (
          <Step1DataSource
            report={virtualReport}
            onUpdate={handleReportUpdate}
            onUploadSie={handleUploadSie}
            onSaveInput={handleSaveInput}
            onNavigateStep={handleNavigateStep}
            nextStep={3}
            guestOrgNumber={guestState?.company?.organizationNumber}
          />
        );
      case 3:
        // Step 3: Overview (was Step 2)
        // For K3, next step is 3.5 (cash flow), otherwise 4
        return (
          <Step2Overview
            report={virtualReport}
            onUpdate={handleReportUpdate}
            onSaveInput={handleSaveInput}
            onNavigateStep={handleNavigateStep}
            prevStep={2}
            nextStep={getNextPublicStep(3, virtualReport.accountingStandard as "K2" | "K3") || 4}
          />
        );
      case 3.5:
        // Step 3.5: Cash Flow (K3 only)
        return (
          <Step2_5CashFlow
            report={virtualReport}
            onUpdate={handleReportUpdate}
            onSaveInput={handleSaveInput}
            onNavigateStep={handleNavigateStep}
            prevStep={3}
            nextStep={4}
          />
        );
      case 4:
        // Step 4: Complete (was Step 3)
        // For K3, previous step is 3.5 (cash flow), otherwise 3
        return (
          <Step3Complete
            report={virtualReport}
            company={virtualCompany}
            onUpdate={handleReportUpdate}
            onSaveInput={handleSaveInput}
            onNavigateStep={handleNavigateStep}
            guestMode
            guestBoardMembers={guestBoardMembers}
            onGuestBoardMembersChange={handleGuestBoardMembersChange}
            onSaveLocalBoardMembers={handleSaveLocalBoardMembers}
            prevStep={getPreviousPublicStep(4, virtualReport.accountingStandard as "K2" | "K3") || 3}
            nextStep={5}
          />
        );
      case 5:
        // Step 5: Disposition (was Step 4)
        return (
          <Step4Disposition
            report={virtualReport}
            onUpdate={handleReportUpdate}
            onSaveInput={handleSaveInput}
            onNavigateStep={handleNavigateStep}
            prevStep={4}
            nextStep={6}
          />
        );
      case 6:
        // Step 6: Auth & Export (was Step 5)
        return (
          <PublicStep6Auth
            companyInfo={guestState?.company || guestState?.companyInfo || null}
            fiscalYear={guestState?.fiscalYear || null}
            isAuthenticated={isAuthenticated}
            user={user}
            isConverting={isConverting}
            convertError={convertError}
            onShowAuth={() => {
              setAuthView("register");
              setShowAuthModal(true);
            }}
            onShowLogin={() => {
              setAuthView("login");
              setShowAuthModal(true);
            }}
            onConvert={handleConvert}
            onBack={() => handleNavigateStep(5)}
            onRefreshUser={refreshUser}
          />
        );
      default:
        return (
          <GuestCompanyStep
            company={guestState?.company || null}
            onContinue={handleCompanyContinue}
          />
        );
    }
  };

  // Handle accounting standard change for guest mode
  const handleChangeStandard = async (newStandard: "K2" | "K3") => {
    // Update user input with new standard
    // Note: We set accountingStandardConfirmed: true because the user already
    // confirmed the change via the "(byt)" confirmation dialog
    const updatedUserInput = {
      ...userInput,
      accountingStandard: newStandard,
      accountingStandardConfirmed: true,
    };

    // If switching from K3 to K2, remove K3-specific data
    if (newStandard === "K2") {
      delete (updatedUserInput as Record<string, unknown>).cashFlow;
      delete (updatedUserInput as Record<string, unknown>).cashFlowStatement;
      delete (updatedUserInput as Record<string, unknown>).deferredTax;
    }

    setUserInput(updatedUserInput);
    saveGuestWizard({ userInput: updatedUserInput, currentStep: 3 });
    setCurrentStep(3); // Go back to overview step
    window.scrollTo(0, 0);
  };

  return (
    <>
      <PageMeta
        title="Prova gratis - Kvanten"
        description={PUBLIC_PAGE_META.landing.description}
        noIndex
      />
      <WizardLayout
        company={virtualCompany}
        report={virtualReport}
        currentStep={currentStep}
        accountingStandard={(virtualReport.accountingStandard as "K2" | "K3") || "K2"}
        guestMode
        onNavigateStep={handleNavigateStep}
        onReset={(guestState?.company || guestState?.parsedSieData) ? handleReset : undefined}
        onChangeStandard={handleChangeStandard}
      >
        {renderStep()}
      </WizardLayout>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        currentView={authView}
        onViewChange={setAuthView}
      />
    </>
  );
}

// ============================================================================
// Step 6: Auth Required (kept as custom component since it's unique to public)
// ============================================================================

function PublicStep6Auth({
  companyInfo,
  fiscalYear,
  isAuthenticated,
  user,
  isConverting,
  convertError,
  onShowAuth,
  onShowLogin,
  onConvert,
  onBack,
  onRefreshUser,
}: {
  companyInfo: { name: string; organizationNumber: string; type?: string } | null;
  fiscalYear: { start: string; end: string } | null;
  isAuthenticated: boolean;
  user: User | null;
  isConverting: boolean;
  convertError: string | null;
  onShowAuth: () => void;
  onShowLogin: () => void;
  onConvert: () => void;
  onBack: () => void;
  onRefreshUser: () => Promise<void>;
}) {
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [isCheckingVerification, setIsCheckingVerification] = useState(false);

  const emailVerified = user?.emailVerified ?? false;

  // Handle resend verification email
  const handleResendVerification = async () => {
    if (!user?.email) return;

    setIsResending(true);
    setResendError(null);
    setResendSuccess(false);

    try {
      const response = await authApi.resendVerification(user.email);
      if (response.success) {
        setResendSuccess(true);
        toast.success("Verifieringsmail skickat!");
      } else {
        setResendError(response.error?.message || "Kunde inte skicka verifieringsmail");
      }
    } catch {
      setResendError("Ett fel uppstod vid sändning av verifieringsmail");
    } finally {
      setIsResending(false);
    }
  };

  // Check if email has been verified
  const handleCheckVerification = async () => {
    setIsCheckingVerification(true);
    try {
      await onRefreshUser();
    } finally {
      setIsCheckingVerification(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Spara & Exportera
        </h2>
        <p className="text-gray-600">
          {!isAuthenticated
            ? "Skapa ett konto för att spara din årsredovisning och exportera PDF."
            : !emailVerified
            ? "Verifiera din e-postadress för att fortsätta."
            : "Spara din årsredovisning för att fortsätta till betalning och export."}
        </p>
      </div>

      {/* Summary card */}
      <div className="card">
        <div className="flex items-center space-x-4 mb-4">
          <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
            <FileText className="text-primary-600" size={24} />
          </div>
          <div>
            <p className="font-semibold text-gray-900">
              {companyInfo?.name || "Företag"}
            </p>
            <p className="text-sm text-gray-500">
              {companyInfo?.organizationNumber} | Räkenskapsår{" "}
              {fiscalYear?.start} - {fiscalYear?.end}
            </p>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <p className="text-sm font-medium text-gray-900 mb-2">
            Din årsredovisning innehåller:
          </p>
          <ul className="space-y-1 text-sm text-gray-600">
            <li className="flex items-center">
              <CheckCircle className="text-green-500 mr-2 flex-shrink-0" size={14} />
              Resultaträkning med nyckeltal
            </li>
            <li className="flex items-center">
              <CheckCircle className="text-green-500 mr-2 flex-shrink-0" size={14} />
              Balansräkning
            </li>
            <li className="flex items-center">
              <CheckCircle className="text-green-500 mr-2 flex-shrink-0" size={14} />
              Automatiska noter baserat på din bokföring
            </li>
            <li className="flex items-center">
              <CheckCircle className="text-green-500 mr-2 flex-shrink-0" size={14} />
              Förvaltningsberättelse med K2-korrekt format
            </li>
          </ul>
        </div>
      </div>

      {convertError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-sm text-red-700">{convertError}</p>
          </div>
        </div>
      )}

      {isAuthenticated ? (
        emailVerified ? (
          // User is authenticated AND email is verified
          <div className="card text-center py-8">
            {isConverting ? (
              <div className="flex flex-col items-center">
                <Loader2 className="animate-spin text-primary-600 mb-4" size={32} />
                <p className="text-gray-900 font-medium">
                  Sparar din årsredovisning...
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Du skickas vidare om en stund
                </p>
              </div>
            ) : (
              <>
                <CheckCircle className="mx-auto text-green-600 mb-4" size={48} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Allt är klart!
                </h3>
                <p className="text-gray-600 mb-6">
                  Klicka för att spara din årsredovisning och fortsätta till betalning.
                </p>
                <button
                  onClick={onConvert}
                  className="btn btn-primary"
                >
                  Spara och fortsätt
                  <ArrowRight size={20} className="ml-2" />
                </button>
              </>
            )}
          </div>
        ) : (
          // User is authenticated but email NOT verified
          <div className="card text-center py-8">
            <Mail className="mx-auto text-amber-500 mb-4" size={48} />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Verifiera din e-postadress
            </h3>
            <p className="text-gray-600 mb-2">
              Vi har skickat ett verifieringsmail till:
            </p>
            <p className="font-medium text-gray-900 mb-4">
              {user?.email}
            </p>
            <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
              Klicka på länken i mailet för att verifiera din e-postadress.
              Du måste verifiera din e-post innan du kan fortsätta till betalning och export.
            </p>

            {resendSuccess && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg mb-4 flex items-center justify-center gap-2">
                <CheckCircle className="text-green-600" size={16} />
                <p className="text-sm text-green-700">
                  Nytt verifieringsmail har skickats!
                </p>
              </div>
            )}

            {resendError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
                <p className="text-sm text-red-700">{resendError}</p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={handleCheckVerification}
                disabled={isCheckingVerification}
                className="btn btn-primary flex items-center justify-center"
              >
                {isCheckingVerification ? (
                  <Loader2 size={18} className="animate-spin mr-2" />
                ) : (
                  <RefreshCw size={18} className="mr-2" />
                )}
                Jag har verifierat
              </button>
              <button
                onClick={handleResendVerification}
                disabled={isResending}
                className="btn btn-secondary flex items-center justify-center"
              >
                {isResending ? (
                  <Loader2 size={18} className="animate-spin mr-2" />
                ) : (
                  <Mail size={18} className="mr-2" />
                )}
                Skicka nytt mail
              </button>
            </div>

            <p className="text-xs text-gray-500 mt-4">
              Kontrollera din skräppost om du inte hittar mailet.
            </p>
          </div>
        )
      ) : (
        // User is NOT authenticated
        <div className="card text-center py-8">
          <Lock className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Skapa konto för att fortsätta
          </h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Gratis att registrera sig. Du behöver ett konto för att spara din
            årsredovisning och exportera PDF.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={onShowAuth}
              className="btn btn-primary flex items-center justify-center"
            >
              <LogIn size={20} className="mr-2" />
              Skapa konto
            </button>
            <button
              onClick={onShowLogin}
              className="btn btn-secondary"
            >
              Har redan konto? Logga in
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            Genom att skapa ett konto godkänner du våra{" "}
            <a href="/terms" className="text-primary-600 hover:underline">
              användarvillkor
            </a>
            .
          </p>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t border-gray-200">
        <button onClick={onBack} className="btn btn-secondary flex items-center">
          <ArrowLeft size={20} className="mr-2" />
          Tillbaka
        </button>
      </div>
    </div>
  );
}
